var MAX_HEIGHT = 1080;
var MAX_WIDTH = 1920;
var MAX_BYTE_SIZE = 8000000;
var SUPPORTED_MIME_TYPES = ['image/jpeg', 'image/png']; // TODO: .gif out of scope for BEAST-650

export var isBase64 = function isBase64(src) {
  return /^data:\w+;base64/.test(src);
};
/**
 * This method removes the header from a base64 encoded image
 *
 * @method removeBase64Header
 * @param {string} base64String - base64 encoded binary data with header information
 * @returns {string} base64 string without header
 */

export var removeBase64Header = function removeBase64Header(base64String) {
  return base64String.replace(/^data:.+;base64/, '');
};
export var getBase64FileSize = function getBase64FileSize(base64) {
  return removeBase64Header(base64).length * (3 / 4);
};
/**
 * This method gets the mime type for a given base64 encoded string
 *
 * @method getBase64MimeType
 * @param {string} base64String - a base64 encoded binary data with header information
 * @returns {string} the type (file suffix) of the encoded string or empty string if not found
 */

export var getBase64MimeType = function getBase64MimeType(base64String) {
  var regex = /^data:(.+);base64.*/;
  var matchResult = base64String.match(regex);
  return matchResult.length > 1 ? matchResult[1] : '';
};
/**
  * @function isValidFile
  *
  * @param {File} file The file being uploaded
  *   (@see https://www.w3.org/TR/FileAPI/#file-constructor).
  * @param {number} file.size The size of the file in bytes.
  * @param {string} file.name The name of the file.
  * @param {type} file.type The type of the file.
  *
  * @return {boolean} True if file is supported, false otherwise.
  */

export var isValidFile = function isValidFile(file) {
  if (!file) return false;
  var isBase64Encoded = isBase64(file);
  var size = isBase64Encoded ? getBase64FileSize(file) : file.size;
  var type = isBase64Encoded ? getBase64MimeType(file) : file.type;
  return !(size > MAX_BYTE_SIZE || SUPPORTED_MIME_TYPES.indexOf(type) === -1);
};
/**
 * Parses the contents of the specfied Blob and returns a `data:` URL representing an
 *  image file's data.
 * Essentially the binary image data is encoded as a base 64 string.
 *
 * @method toBase64
 * @param  {Blob | File} blob - a Blob: a file-like object of immutable, raw binary data.
 * @return {Promise.<string>} A promise, which resolves when the blob has been parsed.
 * @type function
 */

export var toBase64 = function toBase64(blob) {
  if (isBase64(blob)) return Promise.resolve(blob);
  return new Promise(function (resolve, reject) {
    if (window.FileReader && window.Blob && blob instanceof Blob) {
      var reader = new window.FileReader();

      reader.onload = function () {
        return resolve(reader.result);
      };

      reader.onerror = function () {
        return reject(new Error('The image information could not be read.'));
      };

      reader.readAsDataURL(blob);
    } else {
      reject(new Error('The File APIs are not fully supported in this browser.'));
    }
  });
};
/**
  * @param {String} imageSource The source content of the image
  *   (e.g. data:image/png;base64,iVBORw0KGg...) or url
  * @return {Promise.<Image>} Promise that is expected to resolve with the image once
  *   content has been loaded.
  */

export var loadImage = function loadImage(imageSource) {
  var image = new Image();
  return new Promise(function (resolve, reject) {
    image.onload = function () {
      return resolve(image);
    };

    image.onerror = function (error) {
      return reject(error);
    };

    image.src = imageSource;
  });
};
/**
 * @param {Image} image The image to resize.
 * @return {Image} A new resized image (if necessary)
 */

var resizeImageInternal = function resizeImageInternal(image) {
  var img = image;
  var canvas = document.getElementById('canvas'); // Scale Height

  if (img.height > MAX_HEIGHT) {
    img.width = img.width * MAX_HEIGHT / img.height;
    img.height = MAX_HEIGHT;
  } // Scale Width


  if (img.width > MAX_WIDTH) {
    img.height = img.height * MAX_WIDTH / img.width;
    img.width = MAX_WIDTH;
  }

  var context = canvas.getContext('2d');
  context.clearRect(0, 0, canvas.width, canvas.height);
  canvas.width = img.width;
  canvas.height = img.height;
  context.drawImage(img, 0, 0, img.width, img.height);
  var resizedImage = new Image(img.width, img.height);
  resizedImage.src = canvas.toDataURL();
  return resizedImage;
};
/**
 * @param {File | string | Image} file The file, image or base64 encoded image to resize.
 * @return {Promise.<Image>} Promise that is expected to resolve with the resized image content.
 */


export var resizeImage = function resizeImage(file) {
  if (file instanceof Image) return Promise.resolve(resizeImageInternal(file));

  if (!isValidFile(file)) {
    return Promise.reject(new Error("cannot resize image ".concat(file.name, " of type ").concat(file.type)));
  }

  return toBase64(file).then(function (imageSource) {
    return loadImage(imageSource);
  }).then(function (image) {
    return resizeImageInternal(image);
  });
};